import React, { useState } from 'react';
import './NonRespondersView.css';

function NonRespondersView({ studentNonRespondents, staffNonRespondents }) {
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Apply search filter to students
  const filteredStudents = studentNonRespondents.filter(student => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      student.name?.toLowerCase().includes(searchLower) ||
      student.id?.toLowerCase().includes(searchLower) ||
      student.program?.toLowerCase().includes(searchLower)
    );
  });
  
  // Apply search filter to staff
  const filteredStaff = staffNonRespondents.filter(staffMember => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      staffMember.name?.toLowerCase().includes(searchLower) ||
      staffMember.username?.toLowerCase().includes(searchLower) ||
      staffMember.email?.toLowerCase().includes(searchLower) ||
      staffMember.title?.toLowerCase().includes(searchLower)
    );
  });
  
  // Combine for "all" view and sort
  const allNonRespondents = [
    ...filteredStudents.map(student => ({ ...student, type: 'student' })),
    ...filteredStaff.map(staff => ({ ...staff, type: 'staff' }))
  ].sort((a, b) => {
    let valueA, valueB;
    
    if (sortBy === 'name') {
      valueA = a.name || '';
      valueB = b.name || '';
    } else if (sortBy === 'type') {
      valueA = a.type || '';
      valueB = b.type || '';
    } else if (sortBy === 'id') {
      valueA = a.type === 'student' ? a.id : a.username || '';
      valueB = b.type === 'student' ? b.id : b.username || '';
    } else if (sortBy === 'program') {
      valueA = a.type === 'student' ? a.program : a.title || '';
      valueB = b.type === 'student' ? b.program : b.title || '';
    }
    
    if (sortDirection === 'asc') {
      return valueA > valueB ? 1 : -1;
    } else {
      return valueA < valueB ? 1 : -1;
    }
  });
  
  // Sort students
  const sortedStudents = [...filteredStudents].sort((a, b) => {
    let valueA, valueB;
    
    if (sortBy === 'name') {
      valueA = a.name || '';
      valueB = b.name || '';
    } else if (sortBy === 'id') {
      valueA = a.id || '';
      valueB = b.id || '';
    } else if (sortBy === 'program') {
      valueA = a.program || '';
      valueB = b.program || '';
    }
    
    if (sortDirection === 'asc') {
      return valueA > valueB ? 1 : -1;
    } else {
      return valueA < valueB ? 1 : -1;
    }
  });
  
  // Sort staff
  const sortedStaff = [...filteredStaff].sort((a, b) => {
    let valueA, valueB;
    
    if (sortBy === 'name') {
      valueA = a.name || '';
      valueB = b.name || '';
    } else if (sortBy === 'username') {
      valueA = a.username || '';
      valueB = b.username || '';
    } else if (sortBy === 'title') {
      valueA = a.title || '';
      valueB = b.title || '';
    }
    
    if (sortDirection === 'asc') {
      return valueA > valueB ? 1 : -1;
    } else {
      return valueA < valueB ? 1 : -1;
    }
  });
  
  // Handle sorting change
  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Default to ascending for new column
      setSortBy(column);
      setSortDirection('asc');
    }
  };
  
  // Generate CSV data for export
  const generateCsvData = () => {
    let csvContent = "";
    
    if (activeTab === 'all' || activeTab === 'students') {
      // Add headers for students
      csvContent += "Type,Name,ID,Program/Title,Email\n";
      
      // Add student data
      const students = activeTab === 'all' ? filteredStudents : sortedStudents;
      for (const student of students) {
        csvContent += `Student,${student.name || ''},${student.id || ''},${student.program || ''},\n`;
      }
    }
    
    if (activeTab === 'all' || activeTab === 'staff') {
      // Add staff data, with headers if there was no student data
      if (csvContent === "") {
        csvContent += "Type,Name,ID,Program/Title,Email\n";
      }
      
      const staffMembers = activeTab === 'all' ? filteredStaff : sortedStaff;
      for (const staff of staffMembers) {
        csvContent += `Staff,${staff.name || ''},${staff.username || ''},${staff.title || ''},${staff.email || ''}\n`;
      }
    }
    
    return csvContent;
  };
  
  // Handle CSV export
  const handleExport = () => {
    const csvData = generateCsvData();
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    // Create file name based on current date and filter
    const date = new Date().toISOString().split('T')[0];
    const fileName = `non-responders-${activeTab}-${date}.csv`;
    
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  return (
    <div className="non-responders-view-container">
      <h1>Non-Responders</h1>
      
      <div className="filters-container">
        <div className="tabs">
          <button 
            className={activeTab === 'all' ? 'active' : ''} 
            onClick={() => setActiveTab('all')}
          >
            All Non-Responders ({studentNonRespondents.length + staffNonRespondents.length})
          </button>
          <button 
            className={activeTab === 'students' ? 'active' : ''} 
            onClick={() => setActiveTab('students')}
          >
            Students ({studentNonRespondents.length})
          </button>
          <button 
            className={activeTab === 'staff' ? 'active' : ''} 
            onClick={() => setActiveTab('staff')}
          >
            Staff ({staffNonRespondents.length})
          </button>
        </div>
        
        <div className="search-box">
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <button className="export-button" onClick={handleExport}>
          Export to CSV
        </button>
      </div>
      
      {activeTab === 'all' && (
        <>
          <div className="count-display">
            <span>Showing {allNonRespondents.length} non-responders</span>
          </div>
          
          {allNonRespondents.length > 0 ? (
            <div className="responsive-table-container">
              <table className="non-responders-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('name')} className={sortBy === 'name' ? `sorted-${sortDirection}` : ''}>
                      Name
                    </th>
                    <th onClick={() => handleSort('type')} className={sortBy === 'type' ? `sorted-${sortDirection}` : ''}>
                      Type
                    </th>
                    <th onClick={() => handleSort('id')} className={sortBy === 'id' ? `sorted-${sortDirection}` : ''}>
                      ID/Username
                    </th>
                    <th onClick={() => handleSort('program')} className={sortBy === 'program' ? `sorted-${sortDirection}` : ''}>
                      Program/Title
                    </th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {allNonRespondents.map((person, index) => (
                    <tr key={index} className={person.type === 'student' ? 'student-row' : 'staff-row'}>
                      <td>{person.name || 'N/A'}</td>
                      <td>
                        <span className={`type-badge ${person.type}`}>
                          {person.type === 'student' ? 'Student' : 'Staff'}
                        </span>
                      </td>
                      <td>{person.type === 'student' ? person.id : person.username || 'N/A'}</td>
                      <td>{person.type === 'student' ? person.program : person.title || 'N/A'}</td>
                      <td>{person.email || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-results">
              <p>No non-responders match your search criteria.</p>
            </div>
          )}
        </>
      )}
      
      {activeTab === 'students' && (
        <>
          <div className="count-display">
            <span>Showing {sortedStudents.length} student non-responders</span>
          </div>
          
          {sortedStudents.length > 0 ? (
            <div className="responsive-table-container">
              <table className="non-responders-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('name')} className={sortBy === 'name' ? `sorted-${sortDirection}` : ''}>
                      Student Name
                    </th>
                    <th onClick={() => handleSort('id')} className={sortBy === 'id' ? `sorted-${sortDirection}` : ''}>
                      Student ID
                    </th>
                    <th onClick={() => handleSort('program')} className={sortBy === 'program' ? `sorted-${sortDirection}` : ''}>
                      Program
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedStudents.map((student) => (
                    <tr key={student.id}>
                      <td>{student.name || 'N/A'}</td>
                      <td>{student.id || 'N/A'}</td>
                      <td>{student.program || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-results">
              <p>No student non-responders match your search criteria.</p>
            </div>
          )}
        </>
      )}
      
      {activeTab === 'staff' && (
        <>
          <div className="count-display">
            <span>Showing {sortedStaff.length} staff non-responders</span>
          </div>
          
          {sortedStaff.length > 0 ? (
            <div className="responsive-table-container">
              <table className="non-responders-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('name')} className={sortBy === 'name' ? `sorted-${sortDirection}` : ''}>
                      Staff Name
                    </th>
                    <th onClick={() => handleSort('title')} className={sortBy === 'title' ? `sorted-${sortDirection}` : ''}>
                      Title
                    </th>
                    <th onClick={() => handleSort('username')} className={sortBy === 'username' ? `sorted-${sortDirection}` : ''}>
                      Username
                    </th>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedStaff.map((staffMember) => (
                    <tr key={staffMember.username}>
                      <td>{staffMember.name || 'N/A'}</td>
                      <td>{staffMember.title || 'N/A'}</td>
                      <td>{staffMember.username || 'N/A'}</td>
                      <td>{staffMember.email || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-results">
              <p>No staff non-responders match your search criteria.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default NonRespondersView;